import { useLayoutEffect, useRef } from "react";

const DEFAULT_MAX_LINES = 5;
const LINE_HEIGHT_PX = 20;

export function useAutoGrowTextArea(
  value: string,
  {
    maxLines = DEFAULT_MAX_LINES,
    lineHeightPx = LINE_HEIGHT_PX,
    onHeightChange,
  }: { maxLines?: number; lineHeightPx?: number; onHeightChange?: (heightPx: number) => void } = {},
) {
  const ref = useRef<HTMLTextAreaElement | null>(null);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    const fit = () => {
      // scrollHeight never reports less than the height already set.
      element.style.height = "0px";
      const height = Math.min(element.scrollHeight, maxLines * lineHeightPx);
      element.style.height = `${height}px`;
      onHeightChange?.(height);
    };

    fit();

    if (typeof ResizeObserver === "undefined") return;

    // Width decides where the text wraps, so the same value needs a different height once the pane
    // resizes. Only width is acted on: fit() sets the height, which would otherwise re-enter here.
    let lastWidth = element.clientWidth;
    const observer = new ResizeObserver(() => {
      if (element.clientWidth === lastWidth) return;
      lastWidth = element.clientWidth;
      fit();
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, [value, maxLines, lineHeightPx, onHeightChange]);

  return ref;
}
